/**
 * Reference-data loader.
 *
 * Loads JSON files from src/schema/reference-data/ via Vite's dynamic import.
 * Results are cached in memory so each file is only imported once per session.
 *
 * Usage:
 *   const options = await loadRefData('/reference-data/countries.json');
 */

import type { RefDataOption, RefDataFile } from '../types';

// ---------------------------------------------------------------------------
// In-memory cache
// ---------------------------------------------------------------------------

const cache = new Map<string, RefDataOption[]>();

// Country-alias map (loaded lazily alongside country data)
let aliasMap: Record<string, string> | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Load options from a reference-data file.
 * path is the value from field.options_ref, e.g. "/reference-data/countries.json"
 */
export async function loadRefData(path: string): Promise<RefDataOption[]> {
  if (cache.has(path)) {
    return cache.get(path)!;
  }

  // Strip leading slash and "reference-data/" prefix if present, then derive
  // the bare filename (e.g. "countries")
  const normalised = path.replace(/^\/reference-data\//, '').replace(/\.json$/, '');

  let data: RefDataFile;
  try {
    // Vite static analysis requires the import path to contain a literal prefix.
    // Using import() with a computed string works at runtime but may warn in
    // some bundler configs. The ?url suffix is NOT used — we want the JSON object.
    const mod = await importRefDataFile(normalised);
    data = mod as RefDataFile;
  } catch (err) {
    console.warn(`[schema-renderer/loader] Failed to load reference data "${path}":`, err);
    return [];
  }

  const options = data.options ?? [];
  cache.set(path, options);
  return options;
}

/**
 * Resolve a raw value (possibly non-canonical, e.g. "VIETNAM") to its
 * canonical alpha-2 code using the country-aliases map.
 * Returns the original value unchanged if no alias is found.
 */
export async function resolveCountryAlias(raw: string): Promise<string> {
  if (!aliasMap) {
    try {
      const mod = await importRefDataFile('country-aliases');
      aliasMap = (mod as unknown as { aliases: Record<string, string> }).aliases ?? {};
    } catch {
      aliasMap = {};
    }
  }
  return aliasMap[raw.toUpperCase()] ?? raw;
}

/**
 * Synchronous cache lookup — returns undefined if not yet loaded.
 * Useful inside render functions that cannot await.
 */
export function getRefDataSync(path: string): RefDataOption[] | undefined {
  return cache.get(path);
}

// ---------------------------------------------------------------------------
// Country subdivisions (ISO 3166-2), keyed by alpha-2 country code
// ---------------------------------------------------------------------------

type SubdivisionsFile = {
  subdivisions: Record<string, RefDataOption[]>;
};

let subdivisionsMap: Record<string, RefDataOption[]> | null = null;

/**
 * Load the full ISO 3166-2 subdivisions map (keyed by alpha-2 country code)
 * from country-subdivisions.json. Cached after first call.
 */
export async function loadCountrySubdivisions(): Promise<Record<string, RefDataOption[]>> {
  if (subdivisionsMap) return subdivisionsMap;
  try {
    const mod = await importRefDataFile('country-subdivisions');
    subdivisionsMap = (mod as unknown as SubdivisionsFile).subdivisions ?? {};
  } catch (err) {
    console.warn('[schema-renderer/loader] Failed to load country-subdivisions.json:', err);
    subdivisionsMap = {};
  }
  return subdivisionsMap;
}

/**
 * Synchronous lookup of subdivisions for one country code — undefined until
 * loadCountrySubdivisions() has resolved. Returns [] when the country has no
 * subdivisions in the dataset.
 */
export function getCountrySubdivisionsSync(countryCode: string): RefDataOption[] | undefined {
  if (!subdivisionsMap) return undefined;
  return subdivisionsMap[countryCode] ?? [];
}

/**
 * Pre-warm the cache for a list of paths.
 * Call this before rendering a form to avoid async waterfalls.
 */
export async function preloadRefData(paths: string[]): Promise<void> {
  await Promise.all(paths.map((p) => loadRefData(p)));
}

// ---------------------------------------------------------------------------
// File importer — separated so it can be patched in tests
// ---------------------------------------------------------------------------

/**
 * Import a reference-data JSON file by its base name (without extension).
 * Vite's glob import is used so all reference-data files are included in the
 * bundle automatically.
 */

// Eagerly build the module map at bundle time using import.meta.glob.
// This is the Vite-idiomatic way to handle dynamic JSON imports.
const refDataModules = import.meta.glob<RefDataFile>(
  '../../../schema/reference-data/*.json',
  { import: 'default', eager: false },
);

async function importRefDataFile(name: string): Promise<RefDataFile> {
  const key = `../../../schema/reference-data/${name}.json`;
  const loader = refDataModules[key];
  if (!loader) {
    throw new Error(`Reference data file not found: ${name}.json`);
  }
  return loader();
}
